import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    createMenuItems,
    lineOfficialUrl,
    reservationUrl
} from './navigationConfig.js';

const normalizePathname = (path = '') => {
    if (!path) {
        return '/';
    }

    const normalized = path.replace(/\/+$/, '');

    return normalized || '/';
};

const isPathMatch = (pathA, pathB) => normalizePathname(pathA) === normalizePathname(pathB);

const GlobalNavigation = ({
    brandSubtitle = '鍼灸・整体治療院',
    brandTitle = 'TAKAJYU',
    currentRoute,
    includeSections = false,
    navId = 'hero-navigation',
    showFloatingToggle = false
}) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isFloatingMenuVisible, setIsFloatingMenuVisible] = useState(false);
    const history = useHistory();
    const location = useLocation();

    const menuItems = useMemo(
        () => createMenuItems({ includeSections }),
        [includeSections]
    );

    const isSectionActive = useCallback(
        (item) => {
            if (!item.targetId) {
                return false;
            }

            const expectedPath = item.fallbackTo || '/';
            if (!isPathMatch(location.pathname, expectedPath)) {
                return false;
            }

            if (!location.hash && item.targetId === 'hero') {
                return true;
            }

            return location.hash === `#${item.targetId}`;
        },
        [location.pathname, location.hash]
    );

    const closeMenu = useCallback(() => {
        setIsMenuOpen(false);
    }, []);

    const handleMenuToggle = useCallback(() => {
        setIsMenuOpen((prev) => !prev);
    }, []);

    const handleNavigate = useCallback(
        (item) => {
            if (item.targetId) {
                const targetElement = document.getElementById(item.targetId);

                if (targetElement) {
                    history.replace({ pathname: location.pathname, hash: `#${item.targetId}` });
                    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    closeMenu();
                    return;
                }

                history.push({ pathname: item.fallbackTo || '/', hash: `#${item.targetId}` });
                closeMenu();
                return;
            }

            if (item.to) {
                if (!isPathMatch(location.pathname, item.to)) {
                    history.push(item.to);
                }
                closeMenu();
            }
        },
        [closeMenu, history, location.pathname]
    );

    useEffect(() => {
        if (!showFloatingToggle) {
            setIsFloatingMenuVisible(false);
            return undefined;
        }

        const handleScroll = () => {
            setIsFloatingMenuVisible(window.scrollY > 160);
        };

        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [showFloatingToggle]);

    useEffect(() => {
        if (!isMenuOpen) {
            return undefined;
        }

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                closeMenu();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isMenuOpen, closeMenu]);

    useEffect(() => {
        const { body } = document;
        const originalOverflow = body.style.overflow;

        if (isMenuOpen) {
            body.style.overflow = 'hidden';
        }

        return () => {
            body.style.overflow = originalOverflow;
        };
    }, [isMenuOpen]);

    const hamburgerClassNames = `hamburger${isMenuOpen ? ' hamburger--active' : ''}`;
    const floatingHamburgerClassNames = `hamburger floating-hamburger${
        isFloatingMenuVisible && showFloatingToggle ? ' floating-hamburger--visible' : ''
    }${isMenuOpen ? ' hamburger--active' : ''}`;

    return (
        <>
            <header className="hero-header">
                <div className="hero-brand">
                    {brandSubtitle && (
                        <span className="hero-brand__subtitle">{brandSubtitle}</span>
                    )}
                    <div className="hero-brand__headline">
                        <img
                            className="hero-brand__logo"
                            src="/logo.png"
                            alt={brandTitle ? `${brandTitle}のロゴ` : 'ロゴマーク'}
                        />
                        {brandTitle && <h1 className="hero-brand__title">{brandTitle}</h1>}
                    </div>
                </div>
                <button
                    type="button"
                    className={hamburgerClassNames}
                    aria-label="メニュー"
                    aria-controls={navId}
                    aria-expanded={isMenuOpen}
                    onClick={handleMenuToggle}
                >
                    <span />
                    <span />
                    <span />
                </button>
            </header>

            {showFloatingToggle && (
                <button
                    type="button"
                    className={floatingHamburgerClassNames}
                    aria-label="メニュー"
                    aria-controls={navId}
                    aria-expanded={isMenuOpen}
                    onClick={handleMenuToggle}
                >
                    <span />
                    <span />
                    <span />
                </button>
            )}

            <nav
                id={navId}
                className={`global-nav${isMenuOpen ? ' global-nav--open' : ''}`}
                aria-hidden={!isMenuOpen}
            >
                <button
                    type="button"
                    className="global-nav__backdrop"
                    aria-label="メニューを閉じる"
                    onClick={closeMenu}
                />
                <div className="global-nav__panel" role="dialog" aria-modal="true">
                    <div className="global-nav__header">
                        <p className="global-nav__title">メニュー</p>
                        <button
                            type="button"
                            className="global-nav__close"
                            aria-label="メニューを閉じる"
                            onClick={closeMenu}
                        >
                            <span />
                            <span />
                        </button>
                    </div>
                    <ul className="global-nav__list">
                        {menuItems.map((item) => {
                            if (item.items && item.items.length) {
                                const hasActiveChild = item.items.some((child) =>
                                    child.to ? isPathMatch(location.pathname, child.to) : isSectionActive(child)
                                );

                                return (
                                    <li key={item.id} className="global-nav__item global-nav__item--group">
                                        <p className={`global-nav__group-label${hasActiveChild ? ' global-nav__group-label--active' : ''}`}>
                                            {item.label}
                                        </p>
                                        <ul className="global-nav__sublist">
                                            {item.items.map((child) => {
                                                const isActive = child.to
                                                    ? isPathMatch(location.pathname, child.to)
                                                    : isSectionActive(child);

                                                return (
                                                    <li key={child.id} className="global-nav__subitem">
                                                        <button
                                                            type="button"
                                                            className={`global-nav__link${
                                                                isActive ? ' global-nav__link--active' : ''
                                                            }`}
                                                            onClick={() => handleNavigate(child)}
                                                        >
                                                            {child.label}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </li>
                                );
                            }

                            const isActive = item.to
                                ? isPathMatch(location.pathname, item.to)
                                : isSectionActive(item);

                            return (
                                <li key={item.id} className="global-nav__item">
                                    <button
                                        type="button"
                                        className={`global-nav__link${isActive ? ' global-nav__link--active' : ''}`}
                                        onClick={() => handleNavigate(item)}
                                    >
                                        {item.label}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                    <div className="global-nav__footer">
                        <a
                            className="access-actions__button access-actions__button--primary"
                            href={reservationUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeMenu}
                        >
                            ご予約はこちら
                        </a>
                        <a
                            className="access-actions__button access-actions__button--line"
                            href={lineOfficialUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={closeMenu}
                        >
                            LINEで友達追加
                        </a>
                    </div>
                </div>
            </nav>
        </>
    );
};

export default GlobalNavigation;

